import { msg } from '@lingui/macro'

import * as React from 'react'
import { Controller, useFormContext, useFormState } from 'react-hook-form'

import { formatPhoneNumber } from '~logic/helpers/formatPhoneNumber'
import { LinguiMessageDescriptor, useTrans } from '~logic/i18n/useTrans'
import { AnimatedHeight } from '~ui/animatedHeight/AnimatedHeight'
import { InputControl } from '~ui/components/formElements/accessories/InputControl'
import { InputLabel } from '~ui/components/formElements/accessories/InputLabel'
import { InputErrorMessage } from '~ui/components/formElements/accessories/InputMessages'
import { Input, PREVENT_SAFARI_AUTOFILL } from '~ui/components/formElements/Input/Input'
import { Box, GridRow } from '~ui/components/layout/Box'

import { FORM_HORIZONTAL_SPACE, FORM_INPUT_RADIUS } from '../config/form.config'

// weak validations as a compromise between

// at least one number, might start with a `+` symbol
const prefixPattern = /^[+\d]\d+$/
// at least 2 numbers (probably could be more) at the beginning and the end with numbers, spaces or dashes in the middle
const phonePattern = /^[\d]+[\d -]*[\d]+$/

type TPhoneForm = {
  phonePrefix: string
  phoneNumber: string
}

type TProps = NoChildren & {
  isDisabled?: PropsOf<typeof Input>['isDisabled']
  label?: LinguiMessageDescriptor
  rightButton?: React.ReactElement
}

/**
 * Customer’s phone number & prefix inputs.
 *
 * Different label can be used via the `label` prop, defaults to _Phone number_ (translated).
 *
 * ! Needs the `FormProvider` from 'react-hook-form' to be rendered somewhere above,
 * see {@link https://react-hook-form.com/api/formprovider} for examples and details.
 */
export const PhoneInput: React.FC<TProps> = ({
  isDisabled = false,
  label = msg`Phone number`,
  rightButton,
}) => {
  const { trans } = useTrans()
  const { control } = useFormContext<TPhoneForm>()
  const { errors } = useFormState({ control })

  // get error message for any of the phone fields (the number has higher priority)
  const errorMessage = errors.phoneNumber?.message ?? errors.phonePrefix?.message

  // `isDisabled` is only used to display the disabled *filled* input from a previous step (for now)
  const shouldShowRequiredIndicator = !isDisabled

  return (
    <InputControl isRequired={shouldShowRequiredIndicator}>
      <InputLabel>{trans(label)}</InputLabel>
      <GridRow gridTemplateColumns={`6rem 1fr`}>
        <Controller
          control={control}
          name={`phonePrefix`}
          rules={{
            required: {
              value: true,
              message: trans(msg`Fill the phone prefix`),
            },
            maxLength: {
              value: 4,
              message: trans(msg`Prefix is not valid (for sure)`),
            },
            pattern: {
              value: prefixPattern,
              message: trans(msg`Prefix is not valid (for sure)`),
            },
          }}
          render={({
            field: { onChange, onBlur, value, name }, //
            fieldState: { invalid },
            formState: { isSubmitting },
          }) => (
            <Box
              margin={{
                top: `none`, //
                left: `none`,
                bottom: `none`,
                right: FORM_HORIZONTAL_SPACE,
              }}
            >
              <Input
                name={PREVENT_SAFARI_AUTOFILL}
                id={name}
                value={value}
                onBlur={onBlur}
                onChange={onChange}
                placeholder={`+420`}
                isInvalid={invalid}
                inputMode={`tel`}
                autoComplete={'tel-country-code'}
                isDisabled={isDisabled || isSubmitting}
                radius={{
                  topRight: `none`,
                  bottomRight: `none`,
                  topLeft: FORM_INPUT_RADIUS,
                  bottomLeft: FORM_INPUT_RADIUS,
                }}
              />
            </Box>
          )}
        />
        <Controller
          control={control}
          name={`phoneNumber`}
          rules={{
            required: {
              value: true,
              message: trans(msg`Fill the phone number`),
            },
            pattern: {
              value: phonePattern,
              message: trans(msg`Phone number is not valid (for sure)`),
            },
          }}
          render={({
            field: { onChange, onBlur, value, name }, //
            fieldState: { invalid },
            formState: { isSubmitting },
          }) => (
            <Input
              id={name}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
              isInvalid={invalid}
              isDisabled={isDisabled || isSubmitting}
              inputMode={`tel`}
              autoComplete={`tel-national`}
              placeholder={formatPhoneNumber(`123456789`)}
              radius={{
                topLeft: `none`,
                bottomLeft: `none`,
                topRight: rightButton ? `none` : FORM_INPUT_RADIUS,
                bottomRight: rightButton ? `none` : FORM_INPUT_RADIUS,
              }}
            />
          )}
        />
        {rightButton}
      </GridRow>

      <AnimatedHeight isOpen={Boolean(errorMessage)}>
        <InputErrorMessage>{errorMessage ?? null}</InputErrorMessage>
      </AnimatedHeight>
    </InputControl>
  )
}
